import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ProductImage } from '../../ui/ProductImage';
import { HeadingText } from '../shared/HeadingText';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { tierIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { btnOnClay, btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import { AddButton } from '../shared/AddButton';

/**
 * "Waar loopt jouw huid tegenaan?": kies een klacht en zie meteen beide kanten van Beauty Bewust.
 * Links wat je thuis gebruikt (het protocol, los of in één keer in de mand), rechts wat er in
 * de salon bij past. Zo legt dit blok het hele concept uit, zonder dat iemand het hoeft te lezen.
 *
 * De klachten zijn één rij knoppen (op de telefoon schuifbaar); een pil schuift mee naar de
 * keuze. Het advies wisselt met een korte overgang en wordt voorgelezen (live-regio).
 */
export function ConcernFinder() {
  const pages = usePages();
  const {
    concerns,
    routineTotals,
    productsForSkinType,
    routineForConcern,
    treatmentForConcern
  } = useCatalog();
  const [activeId, setActiveId] = useState(concerns[0]?.id);
  const {
    addRoutine
  } = useCartActions();
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const panelId = useId();
  const copy = pages.home.concerns;
  const {
    giftTiers,
    policy
  } = useSite();
  const active = concerns.find(c => c.id === activeId) ?? concerns[0];
  // Zonder klachten óf zonder protocollen (lege backend) is er niets te kiezen.
  const routine = active ? routineForConcern(active) : undefined;
  if (!active || !routine) return null;
  const treatment = treatmentForConcern(active);
  const totals = routineTotals(routine);
  const matching = productsForSkinType(active.skinType).length;
  const gift = tierIn(giftTiers, totals.price);
  const allSoldOut = isAllSoldOut(totals.items);
  return <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="concern" data-id="element-7287" data-mp="n82csmmmo4ih">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7288" data-mp="i9991lx57rvi">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7289" data-mp="i9sgg8p62xva">
          <h2 id="concern" className={`lg:col-span-7 ${sectionHeading} text-ink`} data-id="element-7290" data-mp="dbqxdnrqqyjt">
            <HeadingText heading={copy.heading} data-id="element-7291" data-mp="8bl648pdve29" />
          </h2>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-7292" data-mp="49zrahtagppf">{fill(copy.body)}</p>}
        </div>

        <ul aria-label="Kies je huidklacht" className="-mx-6 mt-10 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7293" data-mp="2fw1piyiuhlmu">
          {concerns.map(c => {
          const isActive = c.id === active.id;
          return <li key={c.id} className="shrink-0" data-id="element-7294" data-mp="2twciq87mncb6">
                <button type="button" aria-pressed={isActive} aria-controls={panelId} onClick={() => setActiveId(c.id)} className={`relative inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full border px-5 text-[15px] transition-[color,border-color] duration-150 ease-soft ${isActive ? 'border-ink text-shell' : 'border-ink/15 text-ink hover:border-ink/50'}`} data-id="element-7295" data-mp="1w9m6cafe0byi">
                  {isActive && <motion.span layoutId="concern-pill" aria-hidden="true" className="absolute inset-[-1px] rounded-full bg-ink" transition={{
                duration: reduce ? 0 : 0.25,
                ease
              }} data-id="element-7296" data-mp="1ifmh72e5f8nw" />}
                  <span className="relative" data-id="element-7297" data-mp="3tyizatd20d1q">{c.label}</span>
                </button>
              </li>;
        })}
        </ul>

        {/* Live-regio: na een keuze hoort een schermlezer dat het advies is gewisseld. */}
        <div id={panelId} aria-live="polite" className="mt-8" data-id="element-7298" data-mp="2qwq6l7kfidh">
          <AnimatePresence mode="wait" initial={false} data-id="element-7299" data-mp="2spwu0i6i2lh">
            <motion.div key={active.id} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease
          }} data-id="element-7300" data-mp="gp3bl1vbvpw9">
              <p className="max-w-3xl text-[17px] leading-relaxed text-muted" data-id="element-7301" data-mp="2diy6rybgfej9">{active.description}</p>

              <div className="mt-8 grid gap-4 lg:grid-cols-12 lg:gap-6" data-id="element-7302" data-mp="szvt6y83lwsv">
                {/* Thuis */}
                <article aria-label={`Thuis bij ${active.label.toLowerCase()}`} className="flex flex-col bg-sand p-6 sm:p-8 lg:col-span-7 lg:p-10" data-id="element-7303" data-mp="xzqi4w8bccba">
                  <p className="text-[13px] text-muted" data-id="element-7304" data-mp="k5zjiiw4ecuc">Thuis</p>
                  <h3 className="mt-1 font-display text-[1.7rem] font-light leading-tight text-ink" data-id="element-7305" data-mp="o8m5w4a0v5k5">{routine.name}</h3>

                  <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-7306" data-mp="a8be8z0w1mxx">
                    {totals.items.map(p => {
                    const soldOut = isSoldOut(p);
                    return <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-7307" data-mp="1batd2d126z8">
                          <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 p-1" data-id="element-7308" data-mp="5bbet6yjnifs" />
                          <div className="min-w-0 flex-1" data-id="element-7309" data-mp="1vo0jd909yw1c">
                            <p className="text-[12px] text-clay" data-id="element-7310" data-mp="1zp3yftai0o1i">{soldOut ? shopLabels.soldOutShort : p.step}</p>
                            <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7311" data-mp="38mkcu8tl85zm">
                              {p.name}
                            </Link>
                          </div>
                          <span className="shrink-0 text-[14px] tabular-nums text-muted" data-id="element-7312" data-mp="2umjbks9euwjm">{euro(p.price)}</span>
                          <AddButton product={p} data-id="element-7313" data-mp="3rcii6h603kzk" />
                        </li>;
                  })}
                  </ul>

                  <div className="mt-auto pt-7" data-id="element-7314" data-mp="3nbgqcleeyb7m">
                    {!allSoldOut && <button type="button" onClick={() => addRoutine(routine.id)} className={`${btnPrice} sm:w-auto sm:min-w-[20rem]`} data-id="element-7315" data-mp="3ii460ngroqko">
                        <span className={btnPriceLabel} data-id="element-7316" data-mp="a45d5up3dlbe">Protocol in de mand</span>
                        <span className={btnPriceAmount} data-id="element-7317" data-mp="a22cmgqbc9sf">{euro(totals.price)}</span>
                      </button>}
                    <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-7318" data-mp="mdux90a1arj4">
                      Gaat {totals.months} mee · met huidplan op papier
                      {gift ? ` · ${gift.title} ligt voor je klaar` : ''}
                      {matching > 0 && <>
                          {' · '}
                          <Link to={`/webshop?huid=${encodeURIComponent(active.skinType)}`} className="underline underline-offset-4 hover:text-ink" data-id="element-7319" data-mp="10dcsnyhu531g">
                            alle {plural(matching, 'product', 'producten')} voor {active.skinType.toLowerCase()}
                          </Link>
                        </>}
                    </p>
                  </div>
                </article>

                {/* In de salon: klei, de diepte van de site. Alleen shell-tekst (5,6:1). */}
                {/* Niet uitrekken tot de hoogte van "Thuis": anders een groot leeg klei-vlak onderaan. */}
                <article aria-label={`In de salon bij ${active.label.toLowerCase()}`} className="flex flex-col bg-clay p-6 text-shell sm:p-8 lg:col-span-5 lg:self-start lg:p-10" data-id="element-7320" data-mp="29cua1ihnurw2">
                  <p className="text-[13px]" data-id="element-7321" data-mp="2jbju9hx14ofh">In de salon</p>
                  {treatment ? <>
                      <h3 className="mt-1 font-display text-[1.7rem] font-light leading-tight" data-id="element-7322" data-mp="2eejo7706jlod">{treatment.name}</h3>
                      {treatment.tagline && <p className="mt-4 text-[16px] leading-relaxed" data-id="element-7323" data-mp="1lpco43l2uo10">{treatment.tagline}</p>}
                      <dl className="mt-6 grid grid-cols-2 border-y border-shell/25 text-[14px]" data-id="element-7324" data-mp="20dqom7v0u0ee">
                        <div className="py-3 pr-4" data-id="element-7325" data-mp="1vnk34gvt2w0d">
                          <dt className="text-[12px]" data-id="element-7326" data-mp="1vlpi72kjb3wa">Duur</dt>
                          <dd className="mt-0.5 font-display text-xl" data-id="element-7327" data-mp="17wwg0odbjzt8">{treatmentDuration(treatment)}</dd>
                        </div>
                        <div className="border-l border-shell/25 py-3 pl-4" data-id="element-7328" data-mp="12y3c9nnu3n69">
                          <dt className="text-[12px]" data-id="element-7329" data-mp="3ekx2z6boy0pq">Prijs</dt>
                          <dd className="mt-0.5 font-display text-xl" data-id="element-7330" data-mp="3jconp0861c7y">{treatmentPrice(treatment)}</dd>
                        </div>
                      </dl>
                    </> : <>
                      <h3 className="mt-1 font-display text-[1.7rem] font-light leading-tight" data-id="element-7331" data-mp="39m3md513xtlc">Liever eerst laten kijken?</h3>
                      <p className="mt-4 text-[16px] leading-relaxed" data-id="element-7332" data-mp="17lastbyjvwz">
                        In een {policy.consult.name.toLowerCase()} van {policy.consult.minutes} minuten kijk ik met je mee en stel ik je plan op.
                      </p>
                    </>}
                  <div className="pt-7" data-id="element-7333" data-mp="3sc1wdqogtwrm">
                    <button type="button" onClick={() => openBooking(treatment?.slug ?? policy.consult.slug)} aria-haspopup="dialog" className={btnOnClay} data-id="element-7334" data-mp="3sf7lv710b0nv">
                      {treatment ? 'Plan deze behandeling' : `Plan een ${policy.consult.name.toLowerCase()}`}
                    </button>
                  </div>
                </article>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}